<template>
  <div>
    <transition mode="out-in" name="fade">
      <template v-if="!loaded">
        <Loader />
      </template>
      <template v-else-if="canDisplay">
        <transition mode="out-in" name="fade">
          <App v-show="displayContent" />
        </transition>
      </template>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watchEffect, computed, Ref } from "vue";
import { setupContext, DebugContext } from "@fresh8/debug-kit/src/debugContext";
import { adtag, getPrototype } from "./prefetch";
import {
  provideBetData,
  provideBetDataRef
} from "@fresh8/ad-data-composition/src/betData";
import {
  provideBrand,
  provideAdTag
} from "@fresh8/ad-data-composition/src/brand";

import { provideDisplayEvents } from "./composition/displayEvents";
import { useGetBetData } from "./hooks/useGetBetData";

import { awaitPromise } from "./lib/awaitPromise";

import { BetData } from "@fresh8/types/lib/betData";
import { AdTag } from "@fresh8/types/src/index";
import { provideTheme } from "@fresh8/ad-data-composition/src/theme";
import { useLocalstorageRef } from "@fresh8/vue-utils/src/useLocalStorageRef";

// simple wrapper to enable HMR
import App from "./App.vue";
import Loader from "./components/Loader.vue";
import { createJssContext, provideJssContext } from "@fresh8/vue-jss/src";
import {
  provideAnalyticsContext,
  createAnalyticsContext
} from "./lib/analytics";

function useDebug({
  adTag,
  adTagLoading,
  betData,
  betDataLoading,
  debugContext
}: {
  adTag: Ref<AdTag>;
  adTagLoading: Ref<boolean>;
  betData: Ref<BetData>;
  betDataLoading: Ref<boolean>;
  debugContext: DebugContext;
}) {
  const viewIDRef = ref();
  let localStorageDataRef;

  watchEffect(() => {
    if (
      viewIDRef.value &&
      debugContext.enableCapture.value &&
      !localStorageDataRef
    ) {
      localStorageDataRef = useLocalstorageRef(
        `captured-id-${viewIDRef.value}`,
        Object,
        {} // shouldn't be used as it should already be set by the watch effect below
      );
    }

    if (localStorageDataRef && localStorageDataRef.value) {
      adTag.value = localStorageDataRef.value.adTagData;
      betData.value = localStorageDataRef.value.betData;
    }
  });

  watchEffect(() => {
    if (adTagLoading.value === false && betDataLoading.value === false) {
      if (debugContext.enableCapture.value) {
        const viewID = betData.value.analytics.viewId;
        viewIDRef.value = viewID;
        debugContext.setEvent(viewID, {
          viewID,
          adTagData: adTag.value,
          betData: betData.value,
          time: Date.now().toString()
        });
      }
    }
  });
}

export default defineComponent({
  name: "AppWrapper",
  components: {
    App,
    Loader
  },
  setup() {
    const displayContent = ref(false);
    const prototype = getPrototype();
    const betDataContext = {
      betData: ref<BetData>(),
      get: useGetBetData,
      loading: ref(false)
    };

    const {
      result: adTag,
      loading: adTagLoading,
      error: adTagError
    } = awaitPromise<AdTag>(adtag);

    const { result: betResult, loading: betDataLoading } = useGetBetData();

    watchEffect(() => {
      if (betResult.value) {
        betDataContext.betData.value = betResult.value;
        if (adTag.value && betResult.value.operator?.name) {
          adTag.value.operator = {
            key: betResult.value.operator.name,
            name: betResult.value.operator.friendlyName
          };
        }
        setTimeout(() => {
          displayContent.value = true;
        }, 1000);
      }
    });

    watchEffect(() => {
      if (prototype && prototype === "strapline") {
        if (adTag.value) {
          adTag.value.size = { ...adTag.value.size, height: 160 };
          adTag.value.responsive = true;
        }
      }
    });

    const jssContext = createJssContext({});
    const debugContext = setupContext();
    const brand = computed(() => adTag.value?.brand);

    provideBetDataRef(betDataContext);

    provideJssContext(jssContext);
    provideBetData(betDataContext.betData);
    provideAdTag(adTag);
    provideBrand(brand);

    const analyticsContext = createAnalyticsContext(
      betDataContext.betData,
      debugContext.enableAnalytics
    );

    provideAnalyticsContext(analyticsContext);
    provideDisplayEvents(betDataContext.betData);
    provideTheme(ref({}));

    watchEffect(() => {
      if (adTagError.value) {
        window.parent.postMessage({ type: "f8_failed_loading" }, "*");
        document.documentElement.style.height = "0px";
        document.documentElement.style.overflow = "hidden";
        document.body.style.height = "0px";
        document.body.style.overflow = "hidden";
      }
    });

    useDebug({
      adTag,
      adTagLoading,
      betData: betDataContext.betData,
      betDataLoading,
      debugContext
    });

    const loaded = computed(
      () => !adTagLoading.value && !betDataLoading.value && !adTagError.value
    );

    return {
      loaded,
      canDisplay: computed(() => adTag.value.brand),
      displayContent
    };
  }
});
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
